import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Flame, Timer, Users, Banknote, AlertOctagon, Package, Activity,
  Wifi, WifiOff, Plus, ChevronRight, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { reportsIntelApi } from '@verlo/shared/services/api';
import wsService from '@verlo/shared/services/websocket';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import SeeMoreLink from '../../../components/reports/SeeMoreLink';

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmtGBP2 = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const STATUS_PILL = {
  good:    { dot: 'bg-signal-ok-line', text: 'text-signal-ok', bg: 'bg-signal-ok-tint border-signal-ok-line', label: 'On target' },
  warn:    { dot: 'bg-signal-warn-line', text: 'text-signal-warn', bg: 'bg-signal-warn-tint border-signal-warn-line', label: 'Watch' },
  bad:     { dot: 'bg-signal-stop-line', text: 'text-signal-stop', bg: 'bg-signal-stop-tint border-signal-stop-line', label: 'Critical' },
  neutral: { dot: 'bg-ink-faint', text: 'text-ink-soft', bg: 'bg-surface-2 border-hairline', label: 'Live' },
};

const EVENT_META = {
  order_placed:  { icon: Plus, tone: 'neutral' },
  kitchen_ready: { icon: CheckCircle2, tone: 'good' },
  payment:       { icon: Banknote, tone: 'neutral' },
  refund:        { icon: XCircle, tone: 'bad' },
  void:          { icon: AlertTriangle, tone: 'warn' },
  late:          { icon: AlertOctagon, tone: 'bad' },
  clock_in:      { icon: Users, tone: 'neutral' },
};

const PulseTile = ({ icon: Icon, label, value, sub, status = 'neutral', testid, seeMoreTo }) => {
  const meta = STATUS_PILL[status] || STATUS_PILL.neutral;
  return (
    <div className="bg-white border border-hairline rounded-lg p-5 relative transition-all duration-300 hover:border-ink-faint hover:shadow-[0_8px_30px_-12px_rgba(32,27,95,0.18)] flex flex-col"
         data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className="text-ink-faint" />
          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">{label}</span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${meta.bg} ${meta.text}`}>
          <span className={`w-1 h-1 rounded-full ${meta.dot} ${status !== 'neutral' ? 'animate-pulse' : ''}`} />
          {meta.label}
        </span>
      </div>
      <div className="font-mono-display text-3xl md:text-4xl font-medium text-ink tracking-tighter">
        {value}
      </div>
      {sub && <p className="text-[12.5px] text-ink-faint mt-1">{sub}</p>}
      {seeMoreTo && (
        <div className="mt-3 pt-3 border-t border-hairline flex justify-end">
          <SeeMoreLink to={seeMoreTo} what={label} testId={`${testid}-see-more`} />
        </div>
      )}
    </div>
  );
};

const EventRow = ({ event }) => {
  const meta = EVENT_META[event.kind] || { icon: Activity, tone: 'neutral' };
  const Icon = meta.icon;
  const tone = event.tone || meta.tone;
  const tonePill = STATUS_PILL[tone] || STATUS_PILL.neutral;
  const at = useMemo(() => {
    try {
      const d = new Date(event.at);
      const secs = Math.floor((Date.now() - d.getTime()) / 1000);
      if (secs < 60) return `${secs}s`;
      if (secs < 3600) return `${Math.floor(secs / 60)}m`;
      return `${Math.floor(secs / 3600)}h`;
    } catch {
      return '';
    }
  }, [event.at]);
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 border-b border-hairline last:border-0 hover:bg-hover-tint/50 transition-colors" data-testid="liveops-event-row" data-event-id={event.id}>
      <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tonePill.bg} ${tonePill.text} shrink-0`}>
        <Icon size={13} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-ink truncate">{event.label}</p>
        <p className="text-[12.5px] text-ink-soft truncate font-mono-display">{event.value}</p>
      </div>
      <span className="text-[11px] uppercase tracking-wider text-ink-faint font-semibold shrink-0">{at} ago</span>
    </li>
  );
};

const LiveOpsPage = () => {
  const [sites, setSites] = useState([]);
  // '' = every site this user can read; the API omits the param (S1-4).
  const [site, setSite] = useFollowedSite('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const lastFetchRef = useRef(0);

  // Load sites once
  useEffect(() => {
    reportsIntelApi.listSites().then((res) => setSites(res.data.sites || [])).catch(() => {});
  }, []);

  // Refetch helper (rate-limited so a burst of WS events doesn't hammer the API)
  const refetch = (force = false) => {
    const now = Date.now();
    if (!force && now - lastFetchRef.current < 2000) return;
    lastFetchRef.current = now;
    reportsIntelApi
      .getLiveOps(site)
      .then((res) => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // Initial + site-change fetch
  useEffect(() => {
    setLoading(true);
    refetch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  // WS subscription
  useEffect(() => {
    wsService.connect();
    const unsubs = [];
    unsubs.push(wsService.subscribe('connection', (d) => setWsConnected(d?.status === 'connected')));
    const refetchEvents = [
      'order_created', 'order_updated', 'order_status_changed',
      'kitchen_order_new', 'kitchen_order_ready',
      'table_updated', 'table_opened', 'table_closed',
    ];
    refetchEvents.forEach((evt) => {
      unsubs.push(wsService.subscribe(evt, () => refetch()));
    });
    setWsConnected(wsService.isConnected());
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling fallback every 8s (lighter when WS is connected)
  useEffect(() => {
    const interval = setInterval(() => refetch(), wsConnected ? 15000 : 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, wsConnected]);

  const snap = data?.snapshot;
  const statuses = data?.statuses || {};
  const events = data?.events || [];

  return (
    <ReportsLayout>
      {/* Live Ops header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="h-9 w-[230px] bg-white border-hairline" data-testid="liveops-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`liveops-site-option-${s.id}`}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Live connection pill */}
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border ${
              wsConnected
                ? 'bg-signal-ok-tint text-signal-ok border-signal-ok-line'
                : 'bg-signal-warn-tint text-signal-warn border-signal-warn-line'
            }`}
            data-testid={wsConnected ? 'liveops-ws-live' : 'liveops-ws-polling'}
          >
            {wsConnected ? <Wifi size={11} /> : <WifiOff size={11} />}
            <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-signal-ok-line animate-pulse' : 'bg-signal-warn-line'}`} />
            {wsConnected ? 'Live · WebSocket' : 'Polling fallback'}
          </span>

        </div>

        {data?.generated_at && (
          <p className="text-[12.5px] text-ink-faint font-mono-display" data-testid="liveops-updated">
            Updated {new Date(data.generated_at).toLocaleTimeString('en-GB')}
          </p>
        )}
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-32 bg-white border border-hairline rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Vital signs grid (2/3 width on lg+) */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="liveops-grid">
            <PulseTile
              testid="liveops-kitchen"
              icon={Flame}
              label="Kitchen load"
              value={snap.kitchen_active}
              sub={`${snap.kitchen_in_prep} in prep · ${snap.kitchen_ready} ready · ${snap.kitchen_sent} sent`}
              status={statuses.kitchen}
            />
            <PulseTile
              testid="liveops-service"
              icon={Timer}
              label="Service pulse"
              value={`${snap.avg_ticket_minutes}m`}
              sub={`Oldest ticket ${snap.oldest_minutes}m`}
              status={statuses.kitchen}
            />
            <PulseTile
              testid="liveops-revenue"
              icon={Activity}
              label="Revenue · last hour"
              value={fmtGBP(snap.revenue_last_hour)}
              sub={`${fmtGBP2(snap.revenue_last_5_min)} last 5 min · ${snap.transactions_last_hour} txns`}
              seeMoreTo="/back-office/reports/ops/hourly-trading"
            />
            <PulseTile
              testid="liveops-tabs"
              icon={Users}
              label="Open tabs"
              value={snap.open_tabs}
              sub={`${snap.covers_now} covers in venue`}
              seeMoreTo="/back-office/reports/service-mode"
            />
            <PulseTile
              testid="liveops-labour"
              icon={Users}
              label="Labour right now"
              value={`${snap.labour_pct_live}%`}
              sub={`${snap.staff_clocked_in} clocked in · ${snap.staff_late_today} late · ${snap.staff_on_break} on break`}
              status={statuses.labour}
              seeMoreTo="/back-office/reports/labour"
            />
            <PulseTile
              testid="liveops-stock"
              icon={Package}
              label="Stock alerts"
              value={snap.stock_critical}
              sub={`${snap.stock_warning} items low · ${snap.wastage_events_today} waste events today`}
              status={statuses.stock}
              seeMoreTo="/back-office/reports/stock"
            />
            <PulseTile
              testid="liveops-refunds"
              icon={Banknote}
              label="Refunds today"
              value={fmtGBP(snap.refunds_today)}
              sub={`${snap.refund_count_today} refunds processed`}
              seeMoreTo="/back-office/reports/ops/discounts-refunds"
            />
            <PulseTile
              testid="liveops-voids"
              icon={AlertTriangle}
              label="Voids today"
              value={snap.voids_today}
              sub={`${snap.payment_failures_today} payment failure${snap.payment_failures_today === 1 ? '' : 's'}`}
              status={statuses.payments}
              seeMoreTo="/back-office/reports/ops/discounts-refunds"
            />
            <PulseTile
              testid="liveops-labour-cost"
              icon={Banknote}
              label="Labour cost today"
              value={fmtGBP(snap.labour_cost_today)}
              sub={`So far · live`}
              seeMoreTo="/back-office/reports/ops/splh"
            />
          </div>

          {/* Live event feed */}
          <div className="bg-white border border-hairline rounded-lg flex flex-col max-h-[calc(100vh-260px)] lg:sticky lg:top-32" data-testid="liveops-ticker">
            <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-signal-ok-line animate-pulse" />
                <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft">Live Event Feed</span>
              </div>
              <span className="text-[12.5px] text-ink-faint font-mono-display">{events.length}</span>
            </div>
            <ul className="flex-1 overflow-y-auto" data-testid="liveops-events">
              {events.length === 0 ? (
                <li className="px-4 py-8 text-center text-[12px] text-ink-faint" data-testid="liveops-quiet">Quiet on the floor. Events appear here as service runs.</li>
              ) : (
                events.map((e) => <EventRow key={e.id} event={e} />)
              )}
            </ul>
            <div className="px-4 py-3 border-t border-hairline">
              <a
                href="/back-office/sales"
                className="text-[12.5px] font-semibold text-ink-soft hover:text-verlo flex items-center justify-between"
                data-testid="liveops-open-sales"
              >
                Open today&apos;s sales detail
                <ChevronRight size={12} />
              </a>
            </div>
          </div>
        </div>
      )}
    </ReportsLayout>
  );
};

export default LiveOpsPage;
